import React, { useState, useEffect, useCallback, useRef } from 'react'

// A swipe has to travel far enough to be deliberate, and be more horizontal
// than vertical, so a scrolling thumb does not change the picture.
const SWIPE_MIN_PX = 40

function PhotoLightbox({ images, initialIndex = 0, onClose }) {
  const [index, setIndex] = useState(initialIndex)
  const touchStart = useRef(null)
  // A swipe ends in a click on the backdrop, which would otherwise close the
  // lightbox the moment you tried to move to the next photo.
  const swiped = useRef(false)

  const next = useCallback(() => setIndex(i => (i + 1) % images.length), [images.length])
  const prev = useCallback(() => setIndex(i => (i - 1 + images.length) % images.length), [images.length])

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') next()
      else if (e.key === 'ArrowLeft') prev()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, next, prev])

  if (!images || images.length === 0) return null

  function onTouchStart(e) {
    const touch = e.changedTouches[0]
    touchStart.current = { x: touch.clientX, y: touch.clientY }
    swiped.current = false
  }

  function onTouchEnd(e) {
    const start = touchStart.current
    touchStart.current = null
    if (!start || images.length < 2) return

    const touch = e.changedTouches[0]
    const movedX = touch.clientX - start.x
    const movedY = touch.clientY - start.y
    if (Math.abs(movedX) < SWIPE_MIN_PX || Math.abs(movedX) <= Math.abs(movedY)) return

    swiped.current = true
    if (movedX < 0) next()
    else prev()
  }

  function onBackdropClick() {
    if (swiped.current) {
      swiped.current = false
      return
    }
    onClose()
  }

  return (
    <div
      data-testid="lightbox-backdrop"
      onClick={onBackdropClick}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100dvw',
        height: '100dvh',
        background: 'rgba(0,0,0,0.92)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'pinch-zoom'
      }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose() }}
        aria-label="Close"
        style={{
          position: 'absolute', top: 'calc(16px + env(safe-area-inset-top))', right: 16,
          width: 44, height: 44, borderRadius: '50%', border: 'none',
          background: 'rgba(255,255,255,0.15)', color: 'white', fontSize: 24, cursor: 'pointer'
        }}
      >
        ×
      </button>
      {images.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); prev() }}
          aria-label="Previous image"
          style={{
            position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
            width: 48, height: 48, borderRadius: '50%', border: 'none',
            background: 'rgba(255,255,255,0.15)', color: 'white', fontSize: 26, cursor: 'pointer'
          }}
        >
          ‹
        </button>
      )}
      <img
        src={images[index]}
        alt="Item photo"
        onClick={(e) => e.stopPropagation()}
        draggable={false}
        style={{ maxWidth: '95vw', maxHeight: '90vh', objectFit: 'contain' }}
      />
      {images.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); next() }}
          aria-label="Next image"
          style={{
            position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
            width: 48, height: 48, borderRadius: '50%', border: 'none',
            background: 'rgba(255,255,255,0.15)', color: 'white', fontSize: 26, cursor: 'pointer'
          }}
        >
          ›
        </button>
      )}
      {images.length > 1 && (
        <div style={{ position: 'absolute', bottom: 'calc(16px + env(safe-area-inset-bottom))', color: 'white', fontSize: 14 }}>
          {index + 1} / {images.length}
        </div>
      )}
    </div>
  )
}

export default PhotoLightbox
